import { useState } from 'react'

import { Box, Button, Icon, PopoverMenu, Table, Text } from '@factorialco/deprecated-design-system'
import { QuotePricingLineItemFragment } from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import RolesAndLevelsEntitySelector from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsSelector'
import { SelectedLevelItem } from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'

interface RoleRowProps {
  item: QuotePricingLineItemFragment
  currency: string
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  locale: string
  onUpdate: (updates: Partial<QuotePricingLineItemFragment>) => void
  onDelete: () => void
}

const RoleRow = ({
  item,
  currency,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  locale,
  onUpdate,
  onDelete,
}: RoleRowProps) => {
  const [open, setOpen] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevelItem>()
  const selectedLevelIdFromItem = isJobCatalogTreeEnabled
    ? item.jobCatalogTreeLevelId
    : item.oldJobCatalogLevelId

  const getDisplayText = (description: string) => {
    if (!selectedLevel) return description || i18n.t('common.select')

    if (selectedLevel.roleName) {
      return `${selectedLevel.roleName} ${selectedLevel.name}`
    }
    return selectedLevel.name
  }

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box paddingLeft="s24" width="s300">
          {isDraftStatus ? (
            <PopoverMenu type="dialog" open={open} onClick={() => setOpen(!open)}>
              <PopoverMenu.Trigger>
                <Box
                  gap="s8"
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  border={{
                    all: {
                      width: 's2',
                      style: 'solid',
                      color: 'grey400',
                    },
                  }}
                  borderRadius={{
                    all: 'abs008',
                  }}
                  padding="s8"
                  paddingX="s16"
                  cursor="pointer"
                >
                  <Text.Normal ellipsis>{getDisplayText(item.description)}</Text.Normal>
                  <Icon.Small icon="caret-down" color="grey600" />
                </Box>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center" width="s400" hideWhenDetached>
                <Box gap="s16" alignItems="center">
                  <RolesAndLevelsEntitySelector
                    selectedLevelIds={selectedLevelIdFromItem ? [selectedLevelIdFromItem] : []}
                    setValues={(partial) => {
                      if (partial.levels) {
                        const levelKey = isJobCatalogTreeEnabled
                          ? 'jobCatalogTreeLevelId'
                          : 'oldJobCatalogLevelId'
                        setSelectedLevel(partial.levels[0])
                        onUpdate({
                          [levelKey]: partial.levels[0]?.id ?? undefined,
                        })
                      }
                    }}
                    existingResourceJobLevelIds={[]}
                    isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
                    isSingleSelector
                  />
                </Box>
              </PopoverMenu.Content>
            </PopoverMenu>
          ) : (
            <Text size="300" color="grey1000">
              {item.description || '-'}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={'hr'} // TODO: Translate
                onChange={(value) => onUpdate({ ['quantity']: Number(value) })}
                value={(item.quantity || 0).toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {item.quantity || '-'}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.unitCostCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.unitPriceCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {`${(item.profitCents || 0) / 100}\u00A0%`}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.baseCostCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.basePriceCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      {isDraftStatus && (
        <Table.Cell colSpan={1} width={80}>
          <Button type="secondary" iconName="delete" onClick={onDelete} />
        </Table.Cell>
      )}
    </Table.Row>
  )
}

export default RoleRow
